import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import Select from 'react-select';
import { Alert, Spinner } from '@patternfly/react-core';

import { configurationTargetSelectors, currentConfigurationTargetSet } from '../../store/configurationTarget/configurationTargetSlice';
import { tokenSelectors, credentialsRegenerationRequested, tokenAssociationApproved } from '../../store/token/tokenSlice';
import DaemonConfigItem from './DaemonConfigItem';
import { appConfigSelectors } from '../../store/appConfig/appConfigSlice';
import { selectStyles } from '../../components/client-selector';
import { mvapiClinicalAppRegistrationApplicationClientId } from '../../environments';
import { DaemonConfig } from '../../store/daemon/daemon-config';

const BROWSER_FLOW = "browser";
const TOKEN_FLOW = "token";

/**
 * This page is the form that gets rendered when Cloud configuration is triggered from the Mvision Configurator
 */
const CloudConfigPage = () => {
    const { t } = useTranslation();
    const targetList = useSelector(configurationTargetSelectors.selectConfigurationTargetList) || [];
    const currentTarget = useSelector(configurationTargetSelectors.selectCurrent);
    const isTargetListFetched = useSelector(configurationTargetSelectors.selectIsConfigurationTargetListFetched);
    const daemonCredentials = useSelector(tokenSelectors.selectDaemonCredentials);
    const deploymentInfo = useSelector(appConfigSelectors.selectAppDeploymentInfo);

    const location = useLocation();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const sessionID = useMemo(() => {return params.get("service")}, [params]);
    const token = useMemo(() => {return params.get("token")}, [params]);
    const flow = useMemo(() => {return params.get("flow")}, [params]);

    const dispatch = useDispatch();

    const changeConfigurationTarget = (newTargetId: string) => {
        if (newTargetId !== currentTarget?.userId) {
            const newValue = newTargetId === '' ? undefined : newTargetId;
            dispatch(currentConfigurationTargetSet(newValue));
        }
    }

    // Regenerate credentials whenever a target becomes current (covers both the
    // dropdown pick and the saga's auto-select path).
    const currentTargetUserId = currentTarget?.userId;
    useEffect(() => {
        if (currentTargetUserId && sessionID && flow === BROWSER_FLOW) {
            dispatch(credentialsRegenerationRequested({ appClient: currentTargetUserId, sessionID }));
        }
    }, [currentTargetUserId, sessionID, flow, dispatch]);

    if ((flow !== BROWSER_FLOW) && (flow !== TOKEN_FLOW)) {
        return <div className="logged-in-container">Configuration flow {flow} is not supported!</div>
    }

    return (
        <div className="logged-in-container" style={{ display: 'flex', flexDirection: 'column' }}>
            {
                !!currentTarget && !!daemonCredentials.error && <Alert variant='danger' title="Error happened during the credentials generation!" />
            }
            {!currentTarget && targetList.length > 0 &&
                <Select
                    className="client-selector-dropdown"
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onChange={(selectedOption: any) => {
                        if (selectedOption) {
                            changeConfigurationTarget(selectedOption.value);
                        }
                    }}
                    isDisabled={!!currentTarget}
                    value={currentTarget}
                    options={targetList.map(target => ({ value: target.userId, label: target.userName }))}
                    placeholder={t('selectConfigurationTarget.placeholder')}
                    maxMenuHeight={700}
                    styles={selectStyles}
                />}
            {!currentTarget && isTargetListFetched && targetList.length === 0 &&
                <Alert variant="warning" isInline title={t('cloudConfigPage.noManageableConnectors')} />}
            {!!currentTarget && flow === BROWSER_FLOW && !daemonCredentials.error
                && (!daemonCredentials.key || !daemonCredentials.secret) &&
                <Spinner aria-label="Generating credentials…" />}
            {!!currentTarget && (flow !== BROWSER_FLOW || (!!daemonCredentials.key && !!daemonCredentials.secret)) && <DaemonConfigItem daemonConfig={{
                aeTitle: 'mvdaemon',
                sessionId: sessionID || 'default',
                listenIp: '0.0.0.0',
                listenPort: '11112',
                forwardImages: false,
                auditLog: false,
                maxTries: '5',
                targets: [{
                    ip: "0.0.0.0",
                    port: "104",
                    aeTitle: "VMSFSD"
                }],
                keepMappingInH: '0',
                backendConfigIntervalInS: '30',
                backendPollIntervalInS: '5',
                passThroughAttributes: [],
                retainAttributes: ['ProtocolName', 'SeriesDescription', 'PatientSex'],
                lastModifiedInS: undefined,
                lastOnlineInS: undefined
            }} runningNumber={1} isBootstrap={true} onConfigSaved={(configJson: object) => {
                if (flow === BROWSER_FLOW) {
                    if (window) {
                        const override = {
                            ...configJson,
                            backend_url: deploymentInfo?.backendUrl,
                            ad_backend_id: mvapiClinicalAppRegistrationApplicationClientId,
                            ad_client_id: daemonCredentials.key,
                            ad_client_key: daemonCredentials.secret
                        };
                        const url = `${params.get('callback')}${btoa(JSON.stringify(override))}`;
                        const w = window.open(url);

                        if (w) {
                            w.focus();
                        }
                    }
                }
                else if (flow === TOKEN_FLOW) {
                    // Notify backend that the certain token is associated with that configuration
                    dispatch(tokenAssociationApproved({ token: token || undefined, sessionID: sessionID || undefined }));
                }
            }} />}
        </div>
    );
}

export default CloudConfigPage;
